// components/marketing/FactoryImage.tsx
'use client';

import { useEffect, useRef, useState } from 'react';

type Props = { src?: string; thumbnailSrc?: string; alt: string; className?: string; fit?: 'cover' | 'contain'; eager?: boolean; errorMessage?: string };

export function FactoryImage(props: Props) {
  return <ImageContent key={props.src ?? ''} {...props} />;
}

function ImageContent({ src, thumbnailSrc, alt, className = '', fit = 'cover', eager = false, errorMessage = 'Image unavailable. Reopen to retry.' }: Props) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'failed'>(src ? 'loading' : 'failed');
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const image = imageRef.current;
    if (image?.complete && (image.naturalWidth > 0 || image.currentSrc)) setStatus(image.naturalWidth > 0 ? 'loaded' : 'failed');
  }, []);
  const imageClass = `absolute inset-0 h-full w-full ${fit === 'contain' ? 'object-contain' : 'object-cover'}`;
  return <span data-factory-image="" data-image-state={status} aria-busy={status === 'loading'} className={`relative block overflow-hidden ${className}`}>
    {status === 'loading' && <span aria-hidden="true" data-image-skeleton="" className="absolute inset-0 bg-[#2D2D2D] motion-safe:animate-pulse" />}
    {thumbnailSrc && <img src={thumbnailSrc} alt="" aria-hidden="true" className={imageClass} />}
    {src && status !== 'failed' && <img ref={imageRef} src={src} alt={alt} loading={eager ? 'eager' : 'lazy'} fetchPriority={eager ? 'high' : undefined} decoding="async"
      onLoad={() => setStatus('loaded')} onError={() => setStatus('failed')} style={{ opacity: status === 'loaded' ? 1 : 0 }} className={`${imageClass} motion-safe:transition-opacity motion-safe:duration-200`} />}
    {status === 'failed' && <span role="status" className="absolute inset-x-0 bottom-0 bg-[#2A2A2A] p-2 text-center text-xs text-white/65">{src ? errorMessage : 'No preview image'}</span>}
  </span>;
}
